import { CurrencyPipe } from '@angular/common'
import { AfterViewInit, Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import * as moment from 'moment'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { Subject } from 'rxjs'
import { AppNavRouteLink } from 'src/app/shared/commons/AppNavRouteLink'
import { valueOrDefault } from 'src/app/shared/commons/Utils'
import { OptimizeOptionsEnum } from 'src/app/shared/enums/optimize-options.enum'
import { ClientModel } from 'src/app/shared/interfaces/client-model'
import { ClientService } from 'src/app/shared/services/client.service'
import { DialogService } from 'src/app/shared/services/dialog.service'
import { FinancialService } from 'src/app/shared/services/financial.service'
import { UtilService } from 'src/app/shared/services/util.service'
import { ConfirmDialogViewModel } from '../confirm-dialog/confirm-dialog.view-model'

@Component({
  selector: 'app-optimize-ideal-portfolio',
  templateUrl: './optimize-ideal-portfolio.component.html',
  styleUrls: ['./optimize-ideal-portfolio.component.scss'],
  providers: [CurrencyPipe]
})
export class OptimizeIdealPortfolioComponent implements OnInit, AfterViewInit {

  step = 1
  progress = 33
  confirmResult: Subject<any>
  extra_financial_value = 0
  total_invested = 0
  optional: any = {
    themes: false,
    expectations: false,
    incomeTax: false,
  }
  accounts: any = []
  client = {} as ClientModel
  rb_account = {} as any
  hasGoals: number = null
  invest_redeem: number
  currencyOption = { prefix: 'R$ ', thousands: '.', decimal: ',', suffix: '', precision: 2, allowNegative: true }
  today = moment()

  constructor(
    private clientService: ClientService,
    private currencyPipe: CurrencyPipe,
    private dialogService: DialogService,
    private financialService: FinancialService,
    public modalRef: BsModalRef,
    private router: Router,
    private utilService: UtilService
  ) { }

  ngOnInit() {
    this.clientService.clientData.subscribe(d => {
      if (!d) return
      else this.client = d
    })

    this.confirmResult = new Subject()
  }

  ngAfterViewInit() {
    this.loadPage()
  }

  loadPage() {
    this.getPositionHistory()
    this.getExtraFinancialValues()
    this.getGoals()
  }

  getExtraFinancialValues() {
    this.extra_financial_value = 0
    if (this.rb_account?.is_simulation_account && this.rb_account?.income_value) {
      this.extra_financial_value = this.rb_account.income_value
    } else if (this.rb_account?.is_simulation_account) {
      this.getIncomes()
    } else {
      this.getConnections()
    }
  }

  async getPositionHistory() {
    if (this.rb_account?.uuid) {
      this.total_invested = 0
      let total_pages = 0
      let filter = {
        min_date: this.today.format('YYYYMMDD'),
        max_date: this.today.format('YYYYMMDD'),
        page: 1,
        rows: 50
      }
      do {
        const $res = await this.clientService.getPositionHistory(this.rb_account?.uuid, filter)
        this.total_invested += $res.histories.flatMap(x => x.positions).reduce((total, pos) => total + pos.financial_value, 0)
        total_pages = $res.statistics.total_pages
        filter.page++
      } while (filter.page <= total_pages)
    }
    // valor atualmente na posição Cash-BRL da  posição atual
  }

  async getIncomes() {
    const $res = await this.clientService.getIncomes(this.client.uuid, { rb_account_uuid: this.rb_account.uuid })
    this.extra_financial_value = $res?.incomes?.reduce((total, item) => total + item.value, 0)
  }

  async getConnections() {
    const $res: any = await this.clientService.getConnections()
    const result: any[] = $res.connections?.filter(connection =>
      connection.rb_accounts.some(acc => acc.account_uuid === this.rb_account.uuid)
    );
    this.extra_financial_value = 0
    result.forEach(c => {
      this.extra_financial_value += c.accounts?.reduce((total, item) => total + item.balance, 0)
    })
  }

  getGoals() {
    this.financialService.getPlansBack(this.client.uuid, { rb_account_uuid: this.rb_account.uuid }).then(result => {
      this.hasGoals = result?.goals?.length
    })
  }

  // Necessário para não dar erro na máscara quando usar o backspace
  onInputChange(event: any) {
    if (!event.target.value || event.target.value === 'R$ ') {
      this.invest_redeem = null;
    }
  }

  changeStep(value, percent) {
    this.step = value
    this.progress = percent
  }

  adjustGoals() {
    this.modalRef.hide()
    this.router.navigate([AppNavRouteLink.TABS_FINANCIAL_PLANNING.GOALS])
  }

  adjustThemes() {
    this.modalRef.hide()
    this.router.navigate([AppNavRouteLink.TABS_INVESTMENT.MY_THEMES])
  }

  adjustExpectations() {
    this.modalRef.hide()
    this.router.navigate([AppNavRouteLink.TABS_INVESTMENT.MY_EXPECTATIONS])
  }

  adjustIncomeTax() {
    this.modalRef.hide()
  }

  /**
   * CHECK PROTECTED ROUTER
   * @param url URL
   */
  checkProtectedRouter(url: string) {
    if (this.client) {
      return this.utilService.routerIsProtected(url, this.client);
    } else {
      return this.utilService.routerIsProtected(url);
    }
  }

  finish() {
    let inBody: any = {}
    inBody.keep_position = false
    let model = new ConfirmDialogViewModel()
    model.alertIcon = true
    model.hiddenCancel = true
    model.class = 'modal-lg'
    if (this.rb_account?.is_simulation_account) {
      model.title = 'Simulação de otimização'
      model.msg = `O valor informado não está vinculado a saldo real e será usado apenas nesta simulação.<br>A <strong>execução só poderá ocorrer após a transferência desse valor para a corretora.</strong>`
      this.dialogService.showConfirm(model).subscribe(res => {
        if (res) {
          inBody.incomes_simulated = []
          inBody.incomes_simulated.push(valueOrDefault(this.invest_redeem, 0))
          this.confirmResult.next(inBody)
          this.modalRef.hide()
        }
      })
    } else if (this.invest_redeem > +(this.total_invested + this.extra_financial_value).toFixed(2)) {
      model.title = 'Valor indisponível para investimento'
      model.msg = `O valor informado é superior ao total disponível nas contas vinculadas e na corretora.<br>Antes da execução, será necessário transferir o valor excedente para a corretora.`
      this.dialogService.showConfirm(model).subscribe(res => {
        if (res) {
          inBody.incomes_simulated = []
          inBody.incomes_simulated.push(valueOrDefault(this.invest_redeem, 0))
          this.confirmResult.next(inBody)
          this.modalRef.hide()
        }
      })
    } else if (this.invest_redeem > 0) {
      model.title = 'Transferência necessária antes da execução'
      model.msg = `O valor informado faz parte do saldo disponível nas suas contas vinculadas.<br> Ele será incluído na otimização, mas <strong>a execução só poderá ocorrer após a transferência desse valor para a corretora</strong>.`
      this.dialogService.showConfirm(model).subscribe(res => {
        if (res) {
          inBody.incomes_simulated = []
          inBody.incomes_simulated.push(valueOrDefault(this.invest_redeem, 0))
          this.confirmResult.next(inBody)
          this.modalRef.hide()
        }
      })
    } else if (this.invest_redeem < 0) {
      model.title = 'Resgate identificado'
      model.msg = `O valor negativo ${this.currencyPipe.transform(this.invest_redeem, 'BRL')} indica um pedido de resgate.  O valor ficará disponível na corretora e o resgate deverá ser feito diretamente por lá.`
      this.dialogService.showConfirm(model).subscribe(res => {
        if (res) {
          inBody.goals_simulated = []
          inBody.goals_simulated.push(this.invest_redeem * -1)
          this.confirmResult.next(inBody)
          this.modalRef.hide()
        }
      })
    }
  }

  get routerLink() {
    return AppNavRouteLink
  }

  get OptimizeOptions() {
    return OptimizeOptionsEnum
  }
}
